import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail, Settings, Inbox, Loader2, RefreshCw, FileText, Trash2, ArrowRight,
  CheckCircle2, AlertTriangle, Copy, Plus, X, Filter, AlertCircle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  ready_for_review: { label: "Klart för granskning", color: "bg-blue-50 text-blue-700 border-blue-200" },
  ai_failed: { label: "AI-fel", color: "bg-red-50 text-red-700 border-red-200" },
  filtered_out: { label: "Bortfiltrerad (regel)", color: "bg-zinc-100 text-zinc-600 border-zinc-200" },
  auto_filtered: { label: "Skippad (brus)", color: "bg-amber-50 text-amber-700 border-amber-200" },
  imported: { label: "Importerad", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function fmt(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("sv-SE");
}

export default function MailImport() {
  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, s] = await Promise.all([
        api.get("/mail/inbox"),
        api.get("/mail/settings"),
        api.get("/mail/stats"),
      ]);
      setInbox(a.data);
      setSettings(b.data);
      setStats(s.data);
    } catch (err) {
      toast.error("Kunde inte ladda mail-modulen");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="flex items-start gap-3 mb-7">
        <div className="w-12 h-12 bg-blue-700 text-white rounded-sm flex items-center justify-center">
          <Mail className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">Mailimport · Automatisk ärendeskapelse</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Inkommande mail → Ärenden
          </h1>
          <p className="mt-1 text-sm text-zinc-600 max-w-xl">
            Inkommande bygglovsmail tolkas av AI och konverteras till färdiga ärenden med metadata, bilagor och AI-checklista.
          </p>
        </div>
        <button onClick={loadAll} data-testid="refresh-mail"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-zinc-300 rounded-sm hover:bg-zinc-100">
          <RefreshCw className="w-3.5 h-3.5" /> Uppdatera
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200 mb-6 flex gap-6">
        <Tab active={tab === "inbox"} onClick={() => setTab("inbox")} icon={Inbox}
          label={`Inkorg (${inbox.length})`} testid="tab-inbox" />
        <Tab active={tab === "test"} onClick={() => setTab("test")} icon={FileText}
          label="Testimport" testid="tab-test" />
        <Tab active={tab === "settings"} onClick={() => setTab("settings")} icon={Settings}
          label="Inställningar" testid="tab-settings" />
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Laddar...</div>}

      {!loading && stats && stats.total > 0 && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="mail-stats">
          <StatCard label="Totalt inkomna" value={stats.total} />
          <StatCard label="AI kördes" value={stats.ai_ran} color="blue" />
          <StatCard label="Skippade (brus)" value={stats.auto_filtered} color="amber" sub="Sparad AI-kostnad" />
          <StatCard label="Filtrerade (regel)" value={stats.rule_filtered} />
          <StatCard label="AI-besparing" value={`${stats.savings_pct}%`} color="emerald" sub="Av total mail-volym" />
        </div>
      )}

      {tab === "inbox" && !loading && (
        <InboxView rows={inbox} onSelect={setSelected} onChange={loadAll} />
      )}
      {tab === "test" && !loading && (
        <TestImportView onImported={() => { loadAll(); setTab("inbox"); }} />
      )}
      {tab === "settings" && !loading && settings && (
        <SettingsView settings={settings} onChange={loadAll} />
      )}

      {selected && (
        <DetailModal inboxId={selected} onClose={() => setSelected(null)}
          onCreated={() => { setSelected(null); loadAll(); }} />
      )}
    </div>
  );
}

function Tab({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 pb-3 -mb-px text-sm font-medium border-b-2 transition ${
        active ? "border-blue-700 text-blue-700" : "border-transparent text-zinc-600 hover:text-zinc-900"
      }`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function StatCard({ label, value, sub, color = "zinc" }) {
  const colors = {
    zinc: "border-zinc-200",
    blue: "border-blue-200 bg-blue-50/30",
    amber: "border-amber-200 bg-amber-50/30",
    emerald: "border-emerald-200 bg-emerald-50/30",
  };
  const numColor = {
    zinc: "text-zinc-900",
    blue: "text-blue-700",
    amber: "text-amber-700",
    emerald: "text-emerald-700",
  };
  return (
    <div className={`bg-white border rounded-sm px-4 py-3 ${colors[color]}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${numColor[color]}`} style={{ fontFamily: "Outfit" }}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function InboxView({ rows, onSelect, onChange }) {
  if (rows.length === 0) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-12 text-center">
        <Inbox className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-zinc-700" style={{ fontFamily: "Outfit" }}>Inga inkommande mail än</h3>
        <p className="mt-2 text-sm text-zinc-500">Använd fliken &ldquo;Testimport&rdquo; för att simulera ett inkommande mail.</p>
      </div>
    );
  }

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Ta bort detta mail från inkorgen?")) return;
    try {
      await api.delete(`/mail/inbox/${id}`);
      toast.success("Borttaget");
      onChange();
    } catch (err) { toast.error("Kunde inte ta bort"); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm" data-testid="mail-inbox-table">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-zinc-600">
          <tr>
            <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider">Inkommit</th>
            <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Avsändare</th>
            <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Ämne</th>
            <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">AI-titel</th>
            <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Bilagor</th>
            <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Status</th>
            <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider">Åtgärd</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const s = STATUS_LABEL[r.status] || { label: r.status, color: "bg-zinc-50 text-zinc-600" };
            return (
              <tr key={r.inbox_id} className="border-t border-zinc-100 hover:bg-blue-50/30 cursor-pointer"
                  onClick={() => onSelect(r.inbox_id)} data-testid={`inbox-row-${r.inbox_id}`}>
                <td className="px-5 py-3 text-zinc-700 text-xs tabular-nums">{fmt(r.created_at)}</td>
                <td className="px-3 py-3 text-zinc-700 text-xs truncate max-w-[180px]">{r.sender}</td>
                <td className="px-3 py-3 font-medium text-zinc-900 truncate max-w-[260px]">{r.subject}</td>
                <td className="px-3 py-3 text-zinc-600 text-xs truncate max-w-[260px]">
                  {r.ai_result?.title_suggestion || <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-3 py-3 text-center text-zinc-600">{(r.attachments || []).length}</td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-sm border ${s.color}`}>{s.label}</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={(e) => del(r.inbox_id, e)} title="Ta bort"
                    className="p-1.5 text-zinc-400 hover:text-red-700 hover:bg-red-50 rounded-sm">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TestImportView({ onImported }) {
  const [f, setF] = useState({
    subject: "Bygglovsansökan – tillbyggnad Storgatan 12",
    sender: "anna.svensson@example.se",
    body: "Hej,\n\nJag vill bygga till mitt hus på Storgatan 12 i Stockholm med 30 kvm. Fastighetsbeteckning: Solberga 5:12.\n\nBifogar situationsplan och fasadritning.\n\nMvh Anna Svensson\n070-123 45 67",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/mail/test-import", f);
      toast.success(`Importerat – status: ${STATUS_LABEL[data.status]?.label || data.status}`);
      onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Importen misslyckades");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-6 space-y-3">
        <h3 className="text-base font-medium" style={{ fontFamily: "Outfit" }}>Simulera ett inkommande mail</h3>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Ämne</label>
          <input value={f.subject} onChange={set("subject")} required data-testid="test-subject"
            className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Avsändare</label>
          <input type="email" value={f.sender} onChange={set("sender")} required data-testid="test-sender"
            className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Brödtext</label>
          <textarea value={f.body} onChange={set("body")} required rows={10} data-testid="test-body"
            className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm resize-none font-mono" />
        </div>
        <button type="submit" disabled={submitting} data-testid="test-submit"
          className="px-4 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm inline-flex items-center gap-2 disabled:opacity-60">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Skicka genom AI-pipeline
        </button>
      </form>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-5 text-xs text-blue-900 leading-relaxed h-fit">
        <div className="font-semibold mb-2 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> Så här fungerar det
        </div>
        <ol className="list-decimal list-inside space-y-1.5">
          <li>Mailet körs genom Claude 4.5</li>
          <li>AI extraherar metadata + skapar checklista</li>
          <li>Du granskar förhandsvisningen</li>
          <li>Klicka &ldquo;Skapa ärende&rdquo; → bilagor + AI-data importeras</li>
        </ol>
      </div>
    </div>
  );
}

function SettingsView({ settings, onChange }) {
  const copy = () => {
    navigator.clipboard.writeText(settings.inbound_address);
    toast.success("Adress kopierad");
  };
  const [showAdd, setShowAdd] = useState(false);

  const del = async (rid) => {
    if (!window.confirm("Ta bort regeln?")) return;
    try {
      await api.delete(`/mail/filter-rules/${rid}`);
      onChange();
    } catch { toast.error("Kunde inte ta bort"); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Din inbound-adress</div>
        <div className="flex items-center gap-3 flex-wrap">
          <code className="px-4 py-3 bg-zinc-900 text-emerald-300 rounded-sm font-mono text-sm" data-testid="inbound-address">
            {settings.inbound_address}
          </code>
          <button onClick={copy} data-testid="copy-inbound"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-zinc-300 rounded-sm hover:bg-zinc-100">
            <Copy className="w-3.5 h-3.5" /> Kopiera
          </button>
        </div>
        <p className="mt-3 text-xs text-zinc-600 max-w-xl">
          Vidarebefordra inkommande bygglovsmail till denna adress – AI tolkar och skapar ärende automatiskt.
          Använd ett mail-rule i Outlook/Gmail eller bygglov@-mottagare → vidarebefordra till adressen ovan.
        </p>
        <div className="mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
          <b>Pilot-fas:</b> Domänen <code>{settings.inbound_domain}</code> ska konfigureras med MX-record + Resend/SendGrid Inbound Parse innan adressen blir live. Använd &ldquo;Testimport&rdquo;-fliken under tiden.
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Filter className="w-3 h-3" /> Filtreringsregler</div>
            <h3 className="mt-1 text-base font-medium" style={{ fontFamily: "Outfit" }}>{settings.filter_rules.length} aktiva regler</h3>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="add-rule"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-sm">
            <Plus className="w-3.5 h-3.5" /> Lägg till regel
          </button>
        </div>
        {settings.filter_rules.length === 0 ? (
          <p className="text-sm text-zinc-500 italic">Inga regler – alla mail importeras.</p>
        ) : (
          <ul className="space-y-2">
            {settings.filter_rules.map((r) => (
              <li key={r.rule_id} className="flex items-center gap-3 px-3 py-2.5 border border-zinc-200 rounded-sm">
                <div className="flex-1">
                  <div className="text-sm font-medium">{r.name}</div>
                  <div className="text-xs text-zinc-500">
                    {r.match_type} matchar <code className="bg-zinc-100 px-1 py-0.5 rounded">{r.pattern}</code> → <b className={r.action === "block" ? "text-red-700" : r.action === "prioritize" ? "text-amber-700" : "text-emerald-700"}>{r.action}</b>
                  </div>
                </div>
                <button onClick={() => del(r.rule_id)} className="p-1.5 text-zinc-400 hover:text-red-700 hover:bg-red-50 rounded-sm">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAdd && <AddRuleModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); onChange(); }} />}
    </div>
  );
}

function AddRuleModal({ onClose, onAdded }) {
  const [f, setF] = useState({ name: "", match_type: "subject_keyword", pattern: "", action: "prioritize", enabled: true });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/mail/filter-rules", f);
      toast.success("Regel skapad");
      onAdded();
    } catch { toast.error("Kunde inte skapa regel"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-6 space-y-3 animate-fade-in-up">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Ny filtreringsregel</h3>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Namn</label>
          <input value={f.name} onChange={set("name")} required data-testid="rule-name" placeholder="t.ex. 'Spammare blockerad'"
            className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Matcha mot</label>
          <select value={f.match_type} onChange={set("match_type")} data-testid="rule-match-type"
            className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm bg-white">
            <option value="subject_keyword">Ämnesrad innehåller</option>
            <option value="sender">Avsändare innehåller</option>
            <option value="domain">Avsändar-domän</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Mönster</label>
          <input value={f.pattern} onChange={set("pattern")} required data-testid="rule-pattern" placeholder="t.ex. 'bygglov' eller 'spam.com'"
            className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Åtgärd</label>
          <select value={f.action} onChange={set("action")} data-testid="rule-action"
            className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm bg-white">
            <option value="prioritize">Prioritera (markera viktigt)</option>
            <option value="allow">Tillåt (importera alltid)</option>
            <option value="block">Blockera (filtrera bort)</option>
          </select>
        </div>
        <button type="submit" data-testid="rule-submit"
          className="w-full px-4 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm">
          Skapa regel
        </button>
      </form>
    </div>
  );
}

function DetailModal({ inboxId, onClose, onCreated }) {
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [overrideTitle, setOverrideTitle] = useState("");

  useEffect(() => {
    api.get(`/mail/inbox/${inboxId}`).then((r) => {
      setDoc(r.data);
      setOverrideTitle(r.data.ai_result?.title_suggestion || "");
    });
  }, [inboxId]);

  const create = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/mail/inbox/${inboxId}/create-case`, {
        inbox_id: inboxId,
        override_title: overrideTitle || null,
      });
      toast.success(`Ärende skapat: ${data.ref}`);
      onCreated();
      nav(`/cases/${data.case_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skapa ärende");
    } finally { setSubmitting(false); }
  };

  const reprocess = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/mail/inbox/${inboxId}/reprocess`);
      setDoc({ ...doc, ai_result: data.ai_result, status: "ready_for_review" });
      setOverrideTitle(data.ai_result?.title_suggestion || "");
      toast.success("Bearbetning klar");
    } catch (err) { toast.error("Misslyckades"); }
    finally { setSubmitting(false); }
  };

  if (!doc) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm">
      <Loader2 className="w-6 h-6 animate-spin text-white" />
    </div>
  );

  const ai = doc.ai_result || {};
  const ex = ai.extracted || {};
  const sum = ai.summary || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-4xl bg-white border border-zinc-200 rounded-sm max-h-[90vh] overflow-y-auto" data-testid="mail-detail-modal">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-blue-700">Mail-import · förhandsvisning</div>
            <h3 className="text-lg font-medium truncate max-w-2xl" style={{ fontFamily: "Outfit" }}>{doc.subject}</h3>
            <p className="text-xs text-zinc-500">Från {doc.sender} · {fmt(doc.created_at)}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-sm"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {doc.status === "ai_failed" && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-sm">
              <div className="text-sm font-semibold text-red-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> AI-bearbetning misslyckades</div>
              <p className="mt-1 text-xs text-red-700">{doc.ai_error}</p>
              <button onClick={reprocess} disabled={submitting}
                className="mt-2 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-700 hover:bg-red-800 text-white rounded-sm">
                <RefreshCw className="w-3.5 h-3.5" /> Försök igen
              </button>
            </div>
          )}

          {doc.imported_case_id && (
            <div className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-sm text-sm text-emerald-900 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Ärende redan skapat från detta mail.
              <button onClick={() => nav(`/cases/${doc.imported_case_id}`)} className="ml-auto text-xs underline">Öppna ärendet</button>
            </div>
          )}

          {ai.is_building_permit === false && (
            <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900">
              <b>AI anser inte att detta är ett bygglovsärende:</b> {ai.spam_or_irrelevant_reason}
            </div>
          )}

          {/* AI summary */}
          {ai.is_building_permit && (
            <>
              <Section title="AI-bedömning" testid="ai-summary">
                <Grid label="Föreslagen titel">
                  <input value={overrideTitle} onChange={(e) => setOverrideTitle(e.target.value)}
                    data-testid="override-title"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm font-medium" />
                </Grid>
                <Grid label="Åtgärdstyp"><span className="capitalize">{ai.case_type}</span></Grid>
                <Grid label="Konfidens">{Math.round((ai.confidence || 0) * 100)}%</Grid>
                <Grid label="Vad sökanden vill"><span className="text-zinc-700">{sum.what_applicant_wants}</span></Grid>
              </Section>

              <Section title="Extraherad metadata">
                <Grid label="Sökande">{ex.applicant_name || "—"}</Grid>
                <Grid label="E-post">{ex.applicant_email || "—"}</Grid>
                <Grid label="Telefon">{ex.applicant_phone || "—"}</Grid>
                <Grid label="Org/Personnr">{ex.applicant_org_or_personnummer || "—"}</Grid>
                <Grid label="Fastighet">{ex.property_designation || "—"}</Grid>
                <Grid label="Adress">{ex.address || "—"}</Grid>
              </Section>

              {sum.documents_missing?.length > 0 && (
                <Section title="Handlingar som saknas">
                  <ul className="space-y-1 text-sm">
                    {sum.documents_missing.map((d, i) => (
                      <li key={i} className="flex gap-2 text-amber-800">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {d}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {ai.auto_checklist?.length > 0 && (
                <Section title="Automatisk checklista">
                  <ul className="space-y-1 text-sm">
                    {ai.auto_checklist.map((c, i) => (
                      <li key={i} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-blue-600 flex-shrink-0" /> {c}</li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}

          {/* Attachments */}
          {doc.attachments?.length > 0 && (
            <Section title={`Bilagor (${doc.attachments.length})`}>
              <ul className="space-y-1.5 text-sm">
                {doc.attachments.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-sm">
                    <FileText className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="flex-1 font-medium">{a.filename}</span>
                    <span className="text-xs text-zinc-500">{Math.round((a.size_bytes || 0) / 1024)} kB</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title="Originalmail">
            <pre className="whitespace-pre-wrap text-xs text-zinc-700 bg-zinc-50 p-3 rounded-sm max-h-48 overflow-y-auto border border-zinc-200">{doc.body}</pre>
          </Section>
        </div>

        {/* Actions */}
        {!doc.imported_case_id && ai.is_building_permit && (
          <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-6 py-4 flex items-center justify-between">
            <button onClick={reprocess} disabled={submitting} data-testid="reprocess-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-zinc-300 rounded-sm hover:bg-zinc-100">
              <RefreshCw className="w-3.5 h-3.5" /> Bearbeta om
            </button>
            <button onClick={create} disabled={submitting} data-testid="create-case-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm disabled:opacity-60">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Skapa ärende <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">{title}</h4>
      <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-2">{children}</div>
    </div>
  );
}

function Grid({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div className="text-zinc-500 col-span-1">{label}</div>
      <div className="col-span-2 text-zinc-900">{children}</div>
    </div>
  );
}
